import {ModuleState} from '@gongxigames/framework/slot/fragments/module/ModuleState';
import {ReelsCascadingModule, ReelsCascadingMoveData, ReelsCascadingStates, ReelsCascadingView} from '@gongxigames/framework/slot/modules/reelsCascading';
import {ReelsEvents, ReelsViewEvents, ReelSymbol} from '@gongxigames/framework/slot/modules/reels';
import {ReelConstants, SymbolPosition} from '@gongxigames/framework/slot/utils';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {FlowModuleEvents, SpinButtonEvents} from '@gongxigames/framework/slot';

export class ReelsCascadingFillInSymbolsState extends ModuleState<ReelsCascadingView, ReelsCascadingModule> {
    protected scattersCount = 0;

    public async onEnterState(): Promise<void> {
        this.module.eventManager.emit('General.UI.ENABLE', ReelsCascadingModule.NAME);
        this.scattersCount = 0;
        this.addSymbolsOnTop();
        await this.moveAllSymbols();

        if (!this.module.isAnyCollapseOnSpin()) {
            this.goToBasicState();
        }

        this.module.eventManager.emit(ReelsEvents.SPIN_ENDED);
    }

    public onLeaveState(): void {
    }

    protected setupEvents(): void {
        this.setEventHandler(ReelsViewEvents.ALL_REELS_STOPPED, this.onAllReelsStopped.bind(this));
        this.setEventHandler(SpinButtonEvents.SPIN_BUTTON_PRESSED, this.onSpinButtonPressed.bind(this));
        this.setEventHandler(FlowModuleEvents.START_COLLAPSING, this.onStartCollapsing.bind(this));
    }

    protected onSpinButtonPressed(): void {
        !this.module.isTurboSpin() && this.view.onForceStopReels();
    }

    protected onStartCollapsing(): void {
        this.module.gotoState(ReelsCascadingStates.COLLAPSE);
    }

    protected addSymbolsOnTop(): void {
        const symbolIds = this.module.logic.getSymbolIds();

        this.view.setupReelsLayers();

        symbolIds.forEach((reel, reelIndex) => {
            reel.forEach((symbolID, symbolIndex) => {
                this.view.addSymbol({
                    symbolID,
                    coordinates: {x: reelIndex, y: symbolIndex - this.module.reelsCascadingConfig.distanceInSymbolsToAppear}
                });
            });
        });
    }

    protected async moveAllSymbols(): Promise<void> {
        const symbolIds = this.module.logic.getSymbolIds();
        const reelsPromises: Promise<void>[] = [];

        symbolIds.forEach((reel, reelIndex) => {
            const reelPromises: Promise<void>[] = [];

            reel.forEach((symbolId, symbolIndex) => {
                reelPromises.push(this.moveSymbol(symbolId, reelIndex, symbolIndex));
            });

            reelsPromises.push(
                Promise.all(reelPromises).then(() => {
                    this.emit(ReelsEvents.REEL_START_STOPPING, reelIndex);
                })
            );
        });

        await Promise.all(reelsPromises);
    }

    protected async moveSymbol(symbolId: number, reelIndex: number, symbolIndex: number): Promise<void> {
        const rowAmount = ReelConstants.ROW_AMOUNT;
        const {betweenReelsDelay, betweenReelsDelayTurbo, betweenSymbolsDelay, betweenSymbolsDelayTurbo, distanceInSymbolsToAppear} = this.module.reelsCascadingConfig;
        const [betweenReelsDelayValue, betweenSymbolsDelayValue] = this.module.isTurboSpin()
            ? [betweenReelsDelayTurbo, betweenSymbolsDelayTurbo]
            : [betweenReelsDelay, betweenSymbolsDelay];
        const moveData: ReelsCascadingMoveData = {
            delay: betweenReelsDelayValue * reelIndex + betweenSymbolsDelayValue * (rowAmount - 1 - symbolIndex),
            fromCoordinates: {x: reelIndex, y: symbolIndex - distanceInSymbolsToAppear},
            toCoordinates: {x: reelIndex, y: symbolIndex}
        };

        await this.view.moveSymbol(moveData);
        await Promise.all([
            this.module.isTurboSpin() ? Promise.resolve() : this.view.playLandingBounceTween(moveData.toCoordinates), // play landing sound
            this.playHitAnimation(symbolId, moveData.toCoordinates)
        ]);
    }

    protected onAllReelsStopped(): void {
        if (this.module.logic.getWin() > 0) {
            this.view.killEverythingFromAnimationsLayer();
            this.view.setAllSymbolsVisible(true);
        }
        this.emit(ReelsEvents.ALL_REELS_STOPPED);
    }

    protected playHitAnimation(symbolId: number, coordinates: SymbolPosition): Promise<ReelSymbol | undefined> {
        const reelIndex = coordinates.x;
        const symbolsWithHitAnimation = this.module.getGameConfig().reelsConfig.symbolsWithHitAnimation;
        const scatterIndex = this.module.getGameConfig().reelsConfig.scatterIndex;
        const {minScattersForFreeGames, scatterHitSound, minScattersForFreeGamesRetrigger} = this.module.reelsCascadingConfig;
        const scattersNeeded = this.module.logic.spinData.action === Action.FREESPIN ? minScattersForFreeGamesRetrigger : minScattersForFreeGames;

        if (symbolsWithHitAnimation.includes(symbolId)) {
            return this.view.addHitAnimation(coordinates);
        }

        if (symbolId === scatterIndex) {
            this.scattersCount ++;

            if (this.scattersCount + ReelConstants.REELS_AMOUNT - reelIndex - 1 >= scattersNeeded) {
                this.module.soundPlayer.play(scatterHitSound + this.scattersCount);

                return this.view.addHitAnimation(coordinates);
            }
        }

        return Promise.resolve(undefined);
    }

    protected goToBasicState(): void {
        this.module.gotoState(ReelsCascadingStates.BASIC_STATE);
    }
}